<script setup lang="ts">
import { computed, ref } from 'vue';
import type { ItemRankingGroup } from '@/services/itemRankingGroup.service';
import PolyButton from '@/components/poly/PolyButton.vue';

const $t = window.i18next.t;

// Árbol de grupos/subgrupos con selección múltiple (asociación del ítem a grupos) y gestión del
// árbol (crear grupo raíz, crear sub-grupo, renombrar, eliminar), equivalente en Vue al
// componente Angular ntn-tree usado en itemRankingGroup.modal.jade. El click en el nombre de un
// nodo alterna su asociación (ícono +/-); el lápiz al costado abre la gestión de ESE nodo sin
// tocar su selección, para no mezclar ambas interacciones en el mismo gesto.

const props = defineProps<{
  groups: ItemRankingGroup[];
  modelValue: string[];
}>();

const emit = defineEmits<{
  (event: 'update:modelValue', value: string[]): void;
  (event: 'create-root', name: string): void;
  (event: 'create-subgroup', parentId: string, name: string): void;
  (event: 'rename', groupId: string, name: string): void;
  (event: 'delete', groupId: string): void;
}>();

// grupos raíz (sin parent)
const rootGroups = computed(() => props.groups.filter(group => !group.parent));

// mapa parentId -> subgrupos, armado en una sola pasada (evita refiltrar todo el array por nodo)
const childrenByParent = computed(() => {
  const map: Record<string, ItemRankingGroup[]> = {};
  props.groups.forEach(group => {
    if (!group.parent) return;
    (map[group.parent] ??= []).push(group);
  });
  return map;
});

function isSelected(groupId: string): boolean {
  return props.modelValue.includes(groupId);
}

// alterna la asociación de un grupo/subgrupo al ítem (independiente de la gestión del árbol)
function toggle(groupId: string) {
  emit('update:modelValue', isSelected(groupId)
    ? props.modelValue.filter(id => id !== groupId)
    : [...props.modelValue, groupId]);
}

// _id del nodo cuyo panel de gestión (editar/eliminar/agregar sub-grupo) está abierto, o null
const managingId = ref<string | null>(null);
// _id especial para el formulario "agregar grupo raíz nuevo"
const NEW_ROOT = '__new-root__';
const newGroupName = ref('');

function openCreateRoot() {
  managingId.value = managingId.value === NEW_ROOT ? null : NEW_ROOT;
  newGroupName.value = '';
}

function openCreateSubgroup(parentId: string) {
  managingId.value = `__new-sub-${parentId}`;
  newGroupName.value = '';
}

function openRename(group: ItemRankingGroup) {
  managingId.value = `__rename-${group._id}`;
  newGroupName.value = group.name;
}

function confirmCreateRoot() {
  if (!newGroupName.value.trim()) return;
  emit('create-root', newGroupName.value.trim());
  managingId.value = null;
}

function confirmCreateSubgroup(parentId: string) {
  if (!newGroupName.value.trim()) return;
  emit('create-subgroup', parentId, newGroupName.value.trim());
  managingId.value = null;
}

function confirmRename(groupId: string) {
  if (!newGroupName.value.trim()) return;
  emit('rename', groupId, newGroupName.value.trim());
  managingId.value = null;
}

function remove(groupId: string) {
  emit('delete', groupId);
  managingId.value = null;
}

// cierra el formulario inline de alta/renombrado en curso sin aplicar cambios
function cancelManaging() {
  managingId.value = null;
  newGroupName.value = '';
}
</script>

<template>
  <div class="group-tree-select">
    <div class="tree-add-root">
      <poly-button button-style="Outline" left-icon="fa-solid fa-plus" data-testid="item-editar-agregar-grupo-boton"
        @click="openCreateRoot">
        {{ $t('Agregar nuevo grupo') }}
      </poly-button>
      <div v-if="managingId === NEW_ROOT" class="tree-inline-form">
        <input v-model="newGroupName" type="text" class="text-input" :placeholder="$t('Nombre del grupo')"
          data-testid="item-editar-grupo-nombre-input" @keyup.enter="confirmCreateRoot" @keyup.esc="cancelManaging" />
        <i class="fa-solid fa-check tree-icon-btn" @click="confirmCreateRoot"></i>
        <i class="fa-solid fa-times tree-icon-btn" :title="$t('Cancelar')" data-testid="item-editar-grupo-cancelar-boton"
          @click="cancelManaging"></i>
      </div>
    </div>

    <div v-for="group in rootGroups" :key="group._id" class="tree-node">
      <div class="tree-item" :class="{ accent: isSelected(group._id!) }">
        <i class="tree-check" :class="isSelected(group._id!) ? 'fa-solid fa-circle-check' : 'fa-regular fa-circle'"
          :title="isSelected(group._id!) ? $t('Seleccionado') : $t('Seleccionar')" @click="toggle(group._id!)"></i>
        <span class="tree-item-label" :data-testid="`item-editar-grupo-${group._id}`" @click="toggle(group._id!)">
          {{ group.name }}
        </span>
        <button type="button" class="tree-icon-btn" :title="$t('Agregar sub-grupo')"
          :data-testid="`item-editar-agregar-subgrupo-${group._id}`" @click.stop="openCreateSubgroup(group._id!)">
          <i class="fa-solid fa-plus"></i>
        </button>
        <button type="button" class="tree-icon-btn" :title="$t('Editar')"
          :data-testid="`item-editar-grupo-editar-${group._id}`" @click.stop="openRename(group)">
          <i class="fa-solid fa-pen"></i>
        </button>
        <button type="button" class="tree-icon-btn" :title="$t('Eliminar')"
          :data-testid="`item-editar-grupo-eliminar-${group._id}`" @click.stop="remove(group._id!)">
          <i class="fa-solid fa-trash"></i>
        </button>
      </div>

      <div v-if="managingId === `__new-sub-${group._id}`" class="tree-inline-form">
        <input v-model="newGroupName" type="text" class="text-input" :placeholder="$t('Nombre del sub-grupo')"
          data-testid="item-editar-subgrupo-nombre-input" @keyup.enter="confirmCreateSubgroup(group._id!)"
          @keyup.esc="cancelManaging" />
        <i class="fa-solid fa-plus tree-icon-btn" @click="confirmCreateSubgroup(group._id!)"></i>
        <i class="fa-solid fa-times tree-icon-btn" :title="$t('Cancelar')" data-testid="item-editar-subgrupo-cancelar-boton"
          @click="cancelManaging"></i>
      </div>
      <div v-else-if="managingId === `__rename-${group._id}`" class="tree-inline-form">
        <input v-model="newGroupName" type="text" class="text-input"
          :data-testid="`item-editar-grupo-renombrar-input-${group._id}`" @keyup.enter="confirmRename(group._id!)"
          @keyup.esc="cancelManaging" />
        <i class="fa-solid fa-check tree-icon-btn" @click="confirmRename(group._id!)"></i>
        <i class="fa-solid fa-times tree-icon-btn" :title="$t('Cancelar')" data-testid="item-editar-grupo-renombrar-cancelar-boton"
          @click="cancelManaging"></i>
      </div>

      <div v-if="childrenByParent[group._id!]?.length" class="tree-children">
        <div v-for="subgroup in childrenByParent[group._id!]" :key="subgroup._id" class="tree-child">
          <div class="tree-item" :class="{ accent: isSelected(subgroup._id!) }">
            <i class="tree-check" :class="isSelected(subgroup._id!) ? 'fa-solid fa-circle-check' : 'fa-regular fa-circle'"
              :title="isSelected(subgroup._id!) ? $t('Seleccionado') : $t('Seleccionar')" @click="toggle(subgroup._id!)"></i>
            <span class="tree-item-label" :data-testid="`item-editar-grupo-${subgroup._id}`"
              @click="toggle(subgroup._id!)">
              {{ subgroup.name }}
            </span>
            <button type="button" class="tree-icon-btn" :title="$t('Editar')"
              :data-testid="`item-editar-grupo-editar-${subgroup._id}`" @click.stop="openRename(subgroup)">
              <i class="fa-solid fa-pen"></i>
            </button>
            <button type="button" class="tree-icon-btn" :title="$t('Eliminar')"
              :data-testid="`item-editar-grupo-eliminar-${subgroup._id}`" @click.stop="remove(subgroup._id!)">
              <i class="fa-solid fa-trash"></i>
            </button>
          </div>
          <div v-if="managingId === `__rename-${subgroup._id}`" class="tree-inline-form">
            <input v-model="newGroupName" type="text" class="text-input"
              :data-testid="`item-editar-subgrupo-renombrar-input-${subgroup._id}`"
              @keyup.enter="confirmRename(subgroup._id!)" @keyup.esc="cancelManaging" />
            <i class="fa-solid fa-check tree-icon-btn" @click="confirmRename(subgroup._id!)"></i>
            <i class="fa-solid fa-times tree-icon-btn" :title="$t('Cancelar')"
              data-testid="item-editar-subgrupo-renombrar-cancelar-boton" @click="cancelManaging"></i>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="less" scoped>
.group-tree-select {
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 320px;
  overflow-y: auto;
  padding-right: 4px;
}

.tree-item {
  display: flex;
  align-items: center;
  gap: 4px;
  border: 1px solid #7c3aed;
  border-radius: 8px;
  padding: 6px 10px;

  &.accent {
    background: #e8e6ff;
  }

  .tree-check {
    color: #7c3aed;
    cursor: pointer;
  }
}

.tree-item-label {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 14px;
  cursor: pointer;
}

.tree-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  color: #888;
  font-size: 12px;
  cursor: pointer;
  padding: 4px 6px;
  border-radius: 4px;
  flex: 0 0 auto;

  &:hover {
    background: #d8d4f0;
    color: #444;
  }
}

.tree-children {
  margin-top: 4px;
  padding-left: 20px;
}

.tree-child {
  margin-top: 4px;
  padding-left: 8px;
  border-left: 1px solid rgba(0, 0, 0, 0.2);
}

.tree-inline-form {
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
  padding-left: 10px;
}

.text-input {
  flex: 1 1 auto;
  min-width: 0;
  padding: 6px 8px;
  border: 1px solid #7c3aed;
  border-radius: 4px;
  font: inherit;
  font-size: 13px;
}

.tree-add-root {
  margin-bottom: 8px;
}
</style>
